import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronRight, Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle2, Facebook, Instagram, Linkedin } from "lucide-react";
import { COMPANY, ASSETS } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import useScrollReveal from "../hooks/useScrollReveal";
import useSEO from "../hooks/useSEO";

const EMPTY = { name: "", email: "", phone: "", message: "", sms_consent: false };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();
  useScrollReveal();
  useSEO({
    title: "Contact Us - Screen Enclosure Specialists in Melbourne, FL | Miller & Miller",
    description: "Contact Miller & Miller Services LLC for screen enclosure installation or repair in Melbourne, FL. Call 321-608-0255 or request a fast, free estimate today.",
    keywords: ["screen enclosure company near me", "pool cage contractor Melbourne FL", "contact"],
    path: "/contact",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Please add your name and email", description: "We need these to get back to you." });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      setDone(true);
      setForm(EMPTY);
      toast({ title: "Message sent!", description: "Thanks - we'll be in touch shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or call us directly." });
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    { icon: Phone, label: "Phone", value: COMPANY.phone, href: COMPANY.phoneHref },
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: Clock, label: "Hours", value: COMPANY.hours },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-20 bg-brand-navy text-white overflow-hidden">
        <img src={ASSETS.pool2} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navydark/90 via-brand-navy/85 to-brand-ocean/50" />
        <div className="container-x relative">
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-brand-gold2">Contact Us</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-heading font-extrabold">Get In Touch</h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">We're here to help and answer any question you might have. Reach out - we respond fast.</p>
        </div>
      </section>

      <section className="section-y bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-10 items-start">
          {/* Info */}
          <div className="reveal">
            <h2 className="text-2xl font-heading font-extrabold text-brand-navy">Stay Connected</h2>
            <p className="mt-3 text-brand-slate">Serving homeowners across {COMPANY.serviceArea}. Free, no-obligation estimates.</p>
            <div className="mt-8 space-y-4">
              {contactItems.map((c) => {
                const Icon = c.icon;
                const inner = (
                  <>
                    <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand-mist text-brand-ocean shrink-0">
                      <Icon className="h-6 w-6" />
                    </span>
                    <span>
                      <span className="block text-xs font-heading font-bold uppercase tracking-wider text-brand-slate">{c.label}</span>
                      <span className="block font-heading font-bold text-brand-navy">{c.value}</span>
                    </span>
                  </>
                );
                return c.href ? (
                  <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="flex items-center gap-4 rounded-2xl p-3 hover:bg-brand-mist/60 transition-colors">
                    {inner}
                  </a>
                ) : (
                  <div key={c.label} className="flex items-center gap-4 p-3">{inner}</div>
                );
              })}
            </div>

            <div className="mt-7 flex items-center gap-3">
              <span className="text-sm font-heading font-bold text-brand-navy">Follow Us:</span>
              <a href={COMPANY.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="h-10 w-10 grid place-items-center rounded-full bg-brand-mist text-brand-ocean hover:bg-brand-ocean hover:text-white transition-colors"><Facebook className="h-4 w-4" /></a>
              <a href={COMPANY.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="h-10 w-10 grid place-items-center rounded-full bg-brand-mist text-brand-ocean hover:bg-brand-ocean hover:text-white transition-colors"><Instagram className="h-4 w-4" /></a>
              <a href={COMPANY.social.googleMaps} target="_blank" rel="noopener noreferrer" aria-label="Find us on Google Maps" data-testid="contact-google-maps" className="h-10 w-10 grid place-items-center rounded-full bg-brand-mist text-brand-ocean hover:bg-brand-ocean hover:text-white transition-colors"><MapPin className="h-4 w-4" /></a>
            </div>
          </div>

          {/* Form */}
          <div className="reveal rounded-3xl bg-brand-mist/50 p-7 md:p-9 ring-1 ring-brand-mist">
            {done ? (
              <div className="text-center py-10">
                <CheckCircle2 className="h-16 w-16 text-brand-ocean mx-auto" />
                <h3 className="mt-4 text-2xl font-heading font-extrabold text-brand-navy">Message Sent!</h3>
                <p className="mt-2 text-brand-slate">Thanks for reaching out. Our team will contact you shortly.</p>
                <Button onClick={() => setDone(false)} className="mt-6 h-12 px-7 bg-brand-ocean hover:bg-brand-sky text-white font-heading font-bold">Send Another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <h3 className="text-xl font-heading font-extrabold text-brand-navy">Send Us a Message</h3>
                <div>
                  <Label htmlFor="c-name" className="font-semibold text-brand-navy">Name *</Label>
                  <Input id="c-name" value={form.name} onChange={update("name")} placeholder="Your full name" className="mt-1.5 h-12 bg-white" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="c-email" className="font-semibold text-brand-navy">Email *</Label>
                    <Input id="c-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className="mt-1.5 h-12 bg-white" />
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className="font-semibold text-brand-navy">Phone</Label>
                    <Input id="c-phone" value={form.phone} onChange={update("phone")} placeholder="(321) 000-0000" className="mt-1.5 h-12 bg-white" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="c-msg" className="font-semibold text-brand-navy">Message / Questions</Label>
                  <Textarea id="c-msg" value={form.message} onChange={update("message")} placeholder="Tell us about your project..." rows={5} className="mt-1.5 bg-white" />
                </div>
                <label htmlFor="c-sms-consent" className="flex items-start gap-3 cursor-pointer rounded-xl bg-white ring-1 ring-brand-mist p-4">
                  <input
                    id="c-sms-consent"
                    type="checkbox"
                    checked={form.sms_consent}
                    onChange={(e) => setForm((f) => ({ ...f, sms_consent: e.target.checked }))}
                    data-testid="contact-sms-consent"
                    className="mt-0.5 h-5 w-5 shrink-0 rounded border-brand-slate/40 text-brand-ocean focus:ring-brand-ocean accent-brand-ocean cursor-pointer"
                  />
                  <span className="text-xs leading-relaxed text-brand-slate">
                    By checking this box, you agree to receive SMS messages from Miller &amp; Miller Services LLC
                    related to estimate scheduling, appointment reminders, and service updates. You may reply STOP
                    to opt out at any time. Reply HELP to 321-608-0255 for assistance. Messages and data rates may
                    apply. Message frequency will vary. Learn more on our{" "}
                    <Link to="/privacy-policy" className="font-bold text-brand-ocean hover:underline">Privacy Policy</Link> page and{" "}
                    <Link to="/terms-conditions" className="font-bold text-brand-ocean hover:underline">Terms &amp; Conditions</Link>.
                  </span>
                </label>
                <Button type="submit" disabled={loading} className="w-full h-13 h-12 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold text-base shadow-gold">
                  {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Sending...</> : <><Send className="h-5 w-5 mr-2" /> Submit</>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
